import { useEffect, useMemo, useState } from "react";

interface MarketItem {
  id: string;
  title: string;
  symbol: string;
  value: number;
  changePct: number | null;
  unit: string | null;
  decimals: number;
  source: string;
  sourceUpdatedAt: string | null;
  fetchedAt: string;
}

interface MarketGroup {
  id: string;
  label: string;
  items: MarketItem[];
}

interface MarketTab {
  id: "global" | "jpkr" | "metals" | "ai";
  label: string;
  groups: MarketGroup[];
}

interface MarketSnapshot {
  schemaVersion: number;
  generatedAt: string;
  providerMode: string;
  tabs: MarketTab[];
  unavailable: Array<{ id: string; title: string; symbol: string; reason: string }>;
}

type TabId = MarketTab["id"] | "settings";

const MARKET_FALLBACK_URL = "https://raw.githubusercontent.com/nianqi-tian/leverage-radar/market-data/data/market/latest.json";

const NAV: Array<{ id: TabId; label: string; icon: string }> = [
  { id: "global", label: "全球", icon: "◎" },
  { id: "jpkr", label: "日韩", icon: "◫" },
  { id: "metals", label: "有色", icon: "◇" },
  { id: "ai", label: "AI", icon: "◉" },
  { id: "settings", label: "设置", icon: "⚙" },
];

function isMarketSnapshot(value: unknown): value is MarketSnapshot {
  if (typeof value !== "object" || value === null) return false;
  const item = value as Record<string, unknown>;
  return item.schemaVersion === 1
    && typeof item.generatedAt === "string"
    && item.providerMode === "public-no-key-strict"
    && Array.isArray(item.tabs)
    && Array.isArray(item.unavailable);
}

async function fetchMarketSnapshot(): Promise<MarketSnapshot> {
  const urls = ["/api/market", MARKET_FALLBACK_URL];
  let lastError: Error | null = null;

  for (const url of urls) {
    try {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
      const value: unknown = await response.json();
      if (!isMarketSnapshot(value)) throw new Error(`${url}: invalid market snapshot`);
      return value;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn("market snapshot source failed", { url, error: lastError.message });
    }
  }

  throw lastError ?? new Error("market snapshot unavailable");
}

function formatTime(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
}

function formatValue(item: MarketItem): string {
  const options: Intl.NumberFormatOptions = {
    minimumFractionDigits: item.decimals,
    maximumFractionDigits: item.decimals,
  };
  return new Intl.NumberFormat("zh-CN", options).format(item.value);
}

function ItemCard({ item }: { item: MarketItem }) {
  const change = item.changePct;
  const changeClass = change === null ? "is-flat" : change > 0 ? "is-up" : change < 0 ? "is-down" : "is-flat";
  const changeText = change === null ? "—" : `${change > 0 ? "+" : ""}${change.toFixed(2)}%`;

  return (
    <article className="market-card">
      <div className="market-card-title">{item.title}</div>
      <div className="market-card-main">
        <strong>{formatValue(item)}</strong>
        <span className={changeClass}>{changeText}</span>
      </div>
      <div className="market-card-meta">
        <span>{item.unit ?? item.symbol}</span>
        <span>更新 {formatTime(item.sourceUpdatedAt)}</span>
      </div>
    </article>
  );
}

function MarketSection({ group }: { group: MarketGroup }) {
  return (
    <section className="market-section">
      <h2>{group.label}</h2>
      {group.items.length > 0 ? (
        <div className="market-grid">
          {group.items.map((item) => <ItemCard key={item.id} item={item} />)}
        </div>
      ) : (
        <div className="market-empty-group">暂无符合“免费、无需 API Key、可靠公开来源”条件的数据。</div>
      )}
    </section>
  );
}

function Settings({ snapshot }: { snapshot: MarketSnapshot }) {
  const sourceNames = useMemo(() => {
    const names = new Set<string>();
    snapshot.tabs.forEach((tab) => tab.groups.forEach((group) => group.items.forEach((item) => names.add(item.source))));
    return [...names];
  }, [snapshot]);

  return (
    <div className="market-settings">
      <section>
        <h2>数据策略</h2>
        <div className="market-settings-card">
          <p><b>严格模式</b></p>
          <p>只展示可验证的公开真实数据；找不到可靠的免费无 Key 来源时，不用 Mock，也不自建价格指数冒充市场价格。</p>
        </div>
      </section>
      <section>
        <h2>当前来源</h2>
        <div className="market-settings-card">
          {sourceNames.map((name) => <p key={name}>{name}</p>)}
        </div>
      </section>
      <section>
        <h2>暂不可用</h2>
        <div className="market-settings-card">
          {snapshot.unavailable.length === 0 ? <p>当前抓取项均正常。</p> : snapshot.unavailable.map((item) => (
            <p key={item.id}><b>{item.title}</b><span>{item.symbol}</span></p>
          ))}
        </div>
      </section>
    </div>
  );
}

export function MarketApp() {
  const [active, setActive] = useState<TabId>("global");
  const [snapshot, setSnapshot] = useState<MarketSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    const load = async () => {
      try {
        const data = await fetchMarketSnapshot();
        if (!disposed) {
          setSnapshot(data);
          setError(null);
        }
      } catch {
        if (!disposed) setError("市场数据暂时无法读取，请稍后重试。");
      } finally {
        if (!disposed) setLoading(false);
      }
    };

    void load();
    const timer = window.setInterval(() => void load(), 5 * 60 * 1000);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);

  const tab = active === "settings" ? null : snapshot?.tabs.find((item) => item.id === active) ?? null;

  return (
    <div className="market-page-shell">
      <div className="market-phone">
        <header className="market-topbar">
          <a href="/" className="market-home">xiaohong.trade</a>
          <span>{snapshot ? `更新 ${formatTime(snapshot.generatedAt)}` : "市场魔方"}</span>
        </header>

        <main className="market-content">
          {loading ? <div className="market-state">正在读取市场数据…</div> : null}
          {error ? <div className="market-state is-error">{error}</div> : null}
          {!loading && !error && snapshot && active === "settings" ? <Settings snapshot={snapshot} /> : null}
          {!loading && !error && tab ? tab.groups.map((group) => <MarketSection key={group.id} group={group} />) : null}
        </main>

        <nav className="market-bottom-nav" aria-label="市场分类">
          {NAV.map((item) => (
            <button
              key={item.id}
              type="button"
              className={active === item.id ? "is-active" : ""}
              onClick={() => {
                setActive(item.id);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <span className="market-nav-icon" aria-hidden="true">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
